import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { platformAPI, paymentsAPI } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Download, Mail, Search, Users2, MessageSquare, Link as LinkIcon, Copy } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const PANEL = '#17171E';
const BORDER = '#2A2A33';

const STATUS_META = {
  new: { label: 'New', badge: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  contacted: { label: 'Contacted', badge: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  converted: { label: 'Converted', badge: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  dismissed: { label: 'Dismissed', badge: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40' },
  closed: { label: 'Closed', badge: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40' },
};

const TRANSITION_OPTIONS = ['new', 'contacted', 'converted', 'dismissed'];

function fmt(ts) {
  if (!ts) return '—';
  try {
    return format(parseISO(ts), 'MMM d, yyyy HH:mm');
  } catch {
    return ts;
  }
}

export default function PlatformLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [paymentLinkTier, setPaymentLinkTier] = useState('elara_growth_monthly');
  const [generatingLink, setGeneratingLink] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch the full set so the summary cards and the status
      // filter dropdown reflect global totals; narrowing is done
      // client-side so switching filters never zeroes other counts.
      const { data } = await platformAPI.listLeads({});
      setLeads(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byStatus = statusFilter === 'all'
      ? leads
      : leads.filter((l) => l.status === statusFilter);
    if (!q) return byStatus;
    return byStatus.filter((l) =>
      [l.organisation_name, l.contact_name, l.email]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q)),
    );
  }, [leads, search, statusFilter]);

  const generatePaymentLink = async () => {
    if (!selected) return;
    setGeneratingLink(true);
    try {
      const { data } = await paymentsAPI.generateLeadPaymentLink(selected.id, paymentLinkTier);
      // Reflect server-side changes locally so the dialog shows the new link.
      setSelected((s) => (s ? { ...s, payment_link_url: data.url, payment_link_lookup_key: paymentLinkTier } : s));
      setLeads((ls) => ls.map((l) => (l.id === selected.id ? { ...l, payment_link_url: data.url, payment_link_lookup_key: paymentLinkTier } : l)));
      toast.success('Payment link generated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate payment link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyPaymentLink = (url) => {
    try {
      navigator.clipboard.writeText(url);
      toast.success('Payment link copied');
    } catch {
      toast.error('Could not copy — select the link manually');
    }
  };

  const summary = useMemo(() => {
    const counts = { new: 0, contacted: 0, converted: 0, dismissed: 0 };
    for (const l of leads) {
      if (l.status in counts) counts[l.status] += 1;
    }
    return counts;
  }, [leads]);

  const updateStatus = async (leadId, newStatus) => {
    setUpdating(true);
    try {
      await platformAPI.updateLeadStatus(leadId, newStatus);
      toast.success(`Lead marked as ${STATUS_META[newStatus]?.label || newStatus}`);
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)),
      );
      if (selected?.id === leadId) setSelected({ ...selected, status: newStatus });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Status update failed');
    } finally {
      setUpdating(false);
    }
  };

  const downloadCsv = () => {
    const url = platformAPI.leadsExportUrl(statusFilter !== 'all' ? statusFilter : null);
    // Use a hidden anchor so the browser respects the cookie session.
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-4" data-testid="platform-leads-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TRANSITION_OPTIONS.map((s) => (
          <Card key={s} className="border" style={{ background: PANEL, borderColor: BORDER }}>
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                {STATUS_META[s].label}
              </div>
              <div className="mt-1 text-2xl font-bold text-white tabular-nums" data-testid={`leads-count-${s}`}>
                {summary[s] ?? 0}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="border" style={{ background: PANEL, borderColor: BORDER }}>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by org, contact or email…"
                className="pl-9 bg-[#0F0F15] border-zinc-800 text-zinc-100"
                data-testid="leads-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-[#0F0F15] border-zinc-800 text-zinc-100" data-testid="leads-status-filter">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent className="bg-[#17171E] border-zinc-800 text-zinc-100">
                <SelectItem value="all">All statuses</SelectItem>
                {TRANSITION_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                ))}
                <SelectItem value="closed">Closed (legacy)</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              onClick={downloadCsv}
              data-testid="leads-export-csv-btn"
            >
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border" style={{ background: PANEL, borderColor: BORDER }}>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center text-zinc-500" data-testid="leads-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-zinc-500" data-testid="leads-empty">
              No trial leads yet. New captures from the trial banner will appear here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-zinc-400">
                  <tr className="border-b border-zinc-800">
                    <th className="text-left px-4 py-3 font-medium">Submitted</th>
                    <th className="text-left px-4 py-3 font-medium">Organisation</th>
                    <th className="text-left px-4 py-3 font-medium">Contact</th>
                    <th className="text-left px-4 py-3 font-medium">Team</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-left px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => {
                    const meta = STATUS_META[l.status] || STATUS_META.new;
                    return (
                      <tr
                        key={l.id}
                        className="border-b border-zinc-900 hover:bg-zinc-900/40 cursor-pointer"
                        onClick={() => setSelected(l)}
                        data-testid={`lead-row-${l.id}`}
                      >
                        <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{fmt(l.submitted_at)}</td>
                        <td className="px-4 py-3 text-zinc-100">{l.organisation_name}</td>
                        <td className="px-4 py-3 text-zinc-200">
                          <div>{l.contact_name}</div>
                          <div className="text-xs text-zinc-500">{l.email}</div>
                        </td>
                        <td className="px-4 py-3 text-zinc-300">{l.team_size || '—'}</td>
                        <td className="px-4 py-3">
                          <Badge className={`border ${meta.badge}`}>{meta.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelected(l);
                            }}
                            className="border-zinc-700 text-zinc-200"
                            data-testid={`lead-open-btn-${l.id}`}
                          >
                            Open
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail / status transition dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-[#17171E] border-zinc-800 text-zinc-100 max-w-xl" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{selected?.organisation_name}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Submitted {selected && fmt(selected.submitted_at)}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-md border border-zinc-800 bg-[#0F0F15]">
                  <div className="text-[11px] uppercase text-zinc-500 font-semibold flex items-center gap-1">
                    <Users2 className="h-3 w-3" /> Contact
                  </div>
                  <div className="text-zinc-100 mt-1">{selected.contact_name}</div>
                  <a
                    href={`mailto:${selected.email}`}
                    className="text-xs text-sky-300 hover:underline flex items-center gap-1 mt-1"
                    data-testid="lead-email-link"
                  >
                    <Mail className="h-3 w-3" /> {selected.email}
                  </a>
                </div>
                <div className="p-3 rounded-md border border-zinc-800 bg-[#0F0F15]">
                  <div className="text-[11px] uppercase text-zinc-500 font-semibold">Team size</div>
                  <div className="text-zinc-100 mt-1">{selected.team_size || '—'}</div>
                  <div className="text-[11px] uppercase text-zinc-500 font-semibold mt-2">Source</div>
                  <div className="text-zinc-300 text-sm mt-1">{selected.source || 'upgrade_banner'}</div>
                </div>
              </div>

              {selected.requirements && (
                <div className="p-3 rounded-md border border-zinc-800 bg-[#0F0F15]">
                  <div className="text-[11px] uppercase text-zinc-500 font-semibold flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" /> Requirements
                  </div>
                  <div className="text-sm text-zinc-200 whitespace-pre-wrap mt-1">
                    {selected.requirements}
                  </div>
                </div>
              )}

              <div className="p-3 rounded-md border border-zinc-800 bg-[#0F0F15]">
                <div className="text-[11px] uppercase text-zinc-500 font-semibold">Current status</div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={`border ${(STATUS_META[selected.status] || STATUS_META.new).badge}`}>
                    {(STATUS_META[selected.status] || STATUS_META.new).label}
                  </Badge>
                  {selected.status_updated_at && (
                    <span className="text-xs text-zinc-500">
                      updated {fmt(selected.status_updated_at)}
                      {selected.status_updated_by_name ? ` by ${selected.status_updated_by_name}` : ''}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {TRANSITION_OPTIONS.map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      disabled={updating || selected.status === s}
                      onClick={() => updateStatus(selected.id, s)}
                      className={`${
                        selected.status === s
                          ? 'bg-zinc-800 text-zinc-400 cursor-not-allowed'
                          : 'bg-[#1ABC9C] hover:bg-[#17a98b] text-white'
                      }`}
                      data-testid={`lead-set-status-${s}`}
                    >
                      Mark {STATUS_META[s].label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="p-3 rounded-md border border-zinc-800 bg-[#0F0F15]" data-testid="lead-payment-link-block">
                <div className="text-[11px] uppercase text-zinc-500 font-semibold flex items-center gap-1">
                  <LinkIcon className="h-3 w-3" /> Assisted-sales payment link
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  Generates a Stripe Payment Link for this org. When the customer pays, the webhook
                  activates the subscription and auto-marks this lead as converted.
                </p>
                <div className="flex flex-wrap gap-2 mt-3 items-center">
                  <Select value={paymentLinkTier} onValueChange={setPaymentLinkTier}>
                    <SelectTrigger className="w-48 bg-[#0F0F15] border-zinc-700 text-zinc-100" data-testid="lead-payment-link-tier">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#17171E] border-zinc-700 text-zinc-100">
                      <SelectItem value="elara_starter_monthly">Starter — monthly</SelectItem>
                      <SelectItem value="elara_starter_yearly">Starter — yearly</SelectItem>
                      <SelectItem value="elara_growth_monthly">Growth — monthly</SelectItem>
                      <SelectItem value="elara_growth_yearly">Growth — yearly</SelectItem>
                      <SelectItem value="elara_enterprise_monthly">Enterprise — monthly</SelectItem>
                      <SelectItem value="elara_enterprise_yearly">Enterprise — yearly</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={generatePaymentLink}
                    disabled={generatingLink}
                    className="bg-[#1ABC9C] hover:bg-[#17a98b] text-white"
                    data-testid="lead-generate-payment-link"
                  >
                    {generatingLink ? 'Generating…' : (selected.payment_link_url ? 'Regenerate link' : 'Generate link')}
                  </Button>
                </div>
                {selected.payment_link_url && (
                  <div className="mt-3 flex items-center gap-2">
                    <a
                      href={selected.payment_link_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-sky-300 hover:underline break-all flex-1"
                      data-testid="lead-payment-link-url"
                    >
                      {selected.payment_link_url}
                    </a>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-zinc-700 text-zinc-200 hover:bg-white/5"
                      onClick={() => copyPaymentLink(selected.payment_link_url)}
                      data-testid="lead-copy-payment-link"
                    >
                      <Copy className="h-3 w-3 mr-1" /> Copy
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
